import { Role, UserAccess } from '../types/auth.type';

export const ADMIN_PANEL_ROLES: Role[] = [Role.ADMIN, Role.CUSTOMER_SUCCESS];

export const ALL_DASHBOARD_ROLES: Role[] = [
  Role.ADMIN,
  Role.CUSTOMER_SUCCESS,
  Role.SECTOR_OPERATIONS_MANAGER,
  Role.ACCOUNTANT,
  Role.CLEARING_OFFICER,
  Role.SALES,
  Role.ORGANIZATION,
  Role.ORGANIZATION_USER,
  Role.AUDITOR,
];

export const ORG_CONTENT_ROLES: Role[] = [
  Role.ADMIN,
  Role.CUSTOMER_SUCCESS,
  Role.SECTOR_OPERATIONS_MANAGER,
  Role.ACCOUNTANT,
  Role.CLEARING_OFFICER,
  Role.SALES,
  Role.ORGANIZATION,
  Role.ORGANIZATION_USER,
];

export const INVOICE_PAGE_ROLES: Role[] = [...ORG_CONTENT_ROLES, Role.AUDITOR];

export const REPORT_PAGE_ROLES: Role[] = [...ORG_CONTENT_ROLES, Role.AUDITOR];

export const EXPENSE_LIST_ROLES: Role[] = [
  Role.ADMIN,
  Role.CUSTOMER_SUCCESS,
  Role.SECTOR_OPERATIONS_MANAGER,
  Role.ACCOUNTANT,
  Role.CLEARING_OFFICER,
];

export const WORKFLOW_ROLES: Role[] = [
  Role.ADMIN,
  Role.CUSTOMER_SUCCESS,
  Role.SECTOR_OPERATIONS_MANAGER,
  Role.ACCOUNTANT,
  Role.CLEARING_OFFICER,
];

export function canAccessWorkflowRole(role?: Role): boolean {
  return !!role && WORKFLOW_ROLES.includes(role);
}

export const STAFF_TASK_ROLES: Role[] = [
  Role.ADMIN,
  Role.CUSTOMER_SUCCESS,
  Role.ACCOUNTANT,
  Role.CLEARING_OFFICER,
  Role.SALES,
];

export const ORG_MANAGEMENT_PAGE_ROLES: Role[] = [
  Role.ACCOUNTANT,
  Role.CLEARING_OFFICER,
  Role.SECTOR_OPERATIONS_MANAGER,
  Role.ADMIN,
  Role.CUSTOMER_SUCCESS,
  Role.SALES,
];

export const ORG_MANAGEMENT_INTERNAL_CHAT_ROLES: Role[] = [
  Role.ACCOUNTANT,
  Role.CLEARING_OFFICER,
  Role.SECTOR_OPERATIONS_MANAGER,
];

export function canAccessOrgManagementInternalChat(role?: Role): boolean {
  return !!role && ORG_MANAGEMENT_INTERNAL_CHAT_ROLES.includes(role);
}

export const INTERNAL_CHAT_ROLES: Role[] = [
  Role.ADMIN,
  Role.CUSTOMER_SUCCESS,
  Role.SECTOR_OPERATIONS_MANAGER,
  Role.ACCOUNTANT,
  Role.CLEARING_OFFICER,
];

export const ROLES_WITHOUT_ORG_PICKER: Role[] = [
  Role.ADMIN,
  Role.CUSTOMER_SUCCESS,
  Role.SALES,
  Role.SECTOR_OPERATIONS_MANAGER,
];

export function hasGlobalOrgAccess(role?: Role): boolean {
  return role === Role.ADMIN || role === Role.CUSTOMER_SUCCESS;
}

/** Roles that may close a client task on an organization they can access. */
export const CLIENT_TASK_CLOSE_ROLES: Role[] = [
  Role.ORGANIZATION,
  Role.ORGANIZATION_USER,
  Role.ACCOUNTANT,
  Role.CLEARING_OFFICER,
  Role.SECTOR_OPERATIONS_MANAGER,
  Role.ADMIN,
  Role.CUSTOMER_SUCCESS,
];

export function canCloseClientTasks(role?: Role): boolean {
  return !!role && CLIENT_TASK_CLOSE_ROLES.includes(role);
}

/** Org owner and org user must attach a file before closing a task that requires one. */
export function taskCloseRequiresFile(role?: Role): boolean {
  return role === Role.ORGANIZATION || role === Role.ORGANIZATION_USER;
}

/** True when this organization is anywhere in the user's access, not only the first entry. */
export function canCloseTasksOnOrganization(
  role: Role | undefined,
  access: UserAccess[] | undefined,
  organizationId?: string,
  accountOrganizationId?: string | null,
): boolean {
  if (!organizationId || !canCloseClientTasks(role)) return false;
  if (hasGlobalOrgAccess(role)) return true;
  if (
    accountOrganizationId
    && accountOrganizationId === organizationId
    && taskCloseRequiresFile(role)
  ) {
    return true;
  }
  return (access ?? []).some(
    (entry) => isGlobalAccessOrganizationId(entry.organization_id) || entry.organization_id === organizationId,
  );
}

export function isSectorOperationsManager(role?: Role): boolean {
  return role === Role.SECTOR_OPERATIONS_MANAGER;
}

export function isAccountantAssignableRole(role?: Role): boolean {
  return role === Role.ACCOUNTANT || role === Role.CLEARING_OFFICER;
}

export function canConnectGoogleCalendar(role?: Role): boolean {
  return (
    role === Role.ACCOUNTANT
    || role === Role.CLEARING_OFFICER
    || role === Role.SECTOR_OPERATIONS_MANAGER
  );
}

export function hasOrgStaffAccess(role?: Role): boolean {
  return (
    hasGlobalOrgAccess(role)
    || isSectorOperationsManager(role)
    || role === Role.ACCOUNTANT
    || role === Role.CLEARING_OFFICER
    || role === Role.SALES
  );
}

export function canPerformAdminDelete(role?: Role): boolean {
  return role === Role.ADMIN;
}

export function hasAdminPanelAccess(role?: Role): boolean {
  return hasGlobalOrgAccess(role);
}

export function canViewProfitCenterMonitoring(role?: Role): boolean {
  return role === Role.ADMIN;
}

export function isGlobalAccessOrganizationId(orgId?: string | null): boolean {
  return orgId === 'all' || orgId === '*';
}

export function isAuditor(role?: Role): boolean {
  return role === Role.AUDITOR;
}

/** Shared books-entry checkbox on invoice files. */
export function canMarkInvoiceEntered(role?: Role): boolean {
  return (
    role === Role.ADMIN
    || role === Role.CUSTOMER_SUCCESS
    || role === Role.SECTOR_OPERATIONS_MANAGER
    || role === Role.ACCOUNTANT
    || role === Role.CLEARING_OFFICER
  );
}

export function hasLimitedInvoiceAccess(role?: Role): boolean {
  return role === Role.ORGANIZATION_USER || role === Role.AUDITOR;
}

export function mapJwtOrganizationAccess(
  organizationIds?: string[],
  permissions?: string[],
  customPermissions?: string[],
): UserAccess[] {
  if (!organizationIds?.length) return [];

  if (organizationIds.some(isGlobalAccessOrganizationId)) {
    return [{
      organization_id: 'all',
      team_id: null,
      permissions: permissions || [],
      custom_permissions: customPermissions || [],
    }];
  }

  return organizationIds.map((orgId) => ({
    organization_id: orgId,
    team_id: null,
    permissions: permissions || [],
    custom_permissions: customPermissions || [],
  }));
}
